/**
 * The View that shows the list of the Vendor Payments made by the user's vendor
 */
import { debounce, chain, assign } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import { Button, Icon, HideText } from '@fashionnovateam/nova-ui-toolkit';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import matchProps from '../../Lib/Utils/PropTypes/match-props';
import { billPaymentsExport } from '../../Lib/Utils/Table/table-export';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import BillPaymentsDataGrid from './BillPaymentsDataGrid.tsx';

/**
 * The BillPayments Table Component, called Payments on the portal
 */
class BillPayments extends Component {
  constructor(props) {
    super(props);

    const { getBillPayments, location } = this.props;

    this.state = { exporting: false };

    this.handlePageChange = this.handlePageChange.bind(this);
    this.fetchBillPayments = this.fetchBillPayments.bind(this);
    this.exportPayments = this.exportPayments.bind(this);
    this.fetchBillPaymentsHeader = this.fetchBillPaymentsHeader.bind(this);
    this.removeSortParams = this.removeSortParams.bind(this);

    getBillPayments(this.currentUserId(), location.search.slice(1));
  }

  /**
   * Adds query parameters to the url to update the query to our api and calls
   * getBillPayments to fetch the payments from the database
   *
   * @param {string} name the name of the query param being added to the url
   * @param {string} value the value of the parameter being added to the url
   */
  fetchBillPayments(name, value) {
    const {
      getBillPayments,
      history,
      location: { search: params, pathname },
    } = this.props;

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ [name]: value })
      .thru((p) => (name === 'search' ? assign(p, { page: 1 }) : p))
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getBillPayments(this.currentUserId(), search);
  }

  removeSortParams() {
    const {
      getBillPayments,
      history,
      location: { search: params, pathname },
    } = this.props;

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: undefined, sortOrder: undefined })
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getBillPayments(this.currentUserId(), search);
  }

  fetchBillPaymentsHeader(e, field) {
    const {
      getBillPayments,
      history,
      location: { search: params, pathname },
    } = this.props;

    const sortParams = decodeLocation(params);
    const { sortBy, sortOrder } = sortParams;

    let newSortOrder;
    if (!sortOrder || field !== sortBy) {
      newSortOrder = sortOrder || 'ASC';
    } else {
      newSortOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
    }

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: field, sortOrder: newSortOrder })
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getBillPayments(this.currentUserId(), search);
  }

  /**
   * Updates the url to increment, decrement, or navigate directly to a
   * specific results page
   *
   * @param {number} page
   */
  handlePageChange(page) {
    this.fetchBillPayments('page', page);
  }

  /**
   * Reads the location prop and returns the user id from the url
   *
   * @returns {number} the id of the user
   */
  currentUserId() {
    const { location } = this.props;
    return location.pathname.split('/')[2];
  }

  exportPayments() {
    const userId = this.currentUserId();
    this.setState({ exporting: true });
    billPaymentsExport(userId).then(() => {
      this.setState({ exporting: false });
    });
  }

  render() {
    const { count, location, billPayments, loading } = this.props;
    const params = decodeLocation(location.search);
    const { exporting } = this.state;
    const seachValue = queryString.parse(location.search.slice(1)).search || '';

    return (
      <Page.Container>
        <Header.Major icon="money-dollar-bag" title="Payments" />
        <Header.Minor
          search={{
            value: seachValue,
            onChange: debounce(
              (value) => this.fetchBillPayments('search', value),
              500,
            ),
          }}
        >
          {exporting ? (
            <Icon.LoadingCircle />
          ) : (
            <Button size="sm" color="secondary" onClick={this.exportPayments}>
              <Icon name="download/download-down" />
              <HideText notAbove="lg">Export</HideText>
            </Button>
          )}
        </Header.Minor>
        <Page.Content>
          <BillPaymentsDataGrid
            rows={billPayments}
            count={count}
            loading={loading}
            paginationModel={{
              pageSize: 15,
              page: params.page - 1 ?? 0,
            }}
            pageSizeOptions={[15]}
            onPaginationModelChange={(model) => {
              if (params.page) {
                this.handlePageChange(model.page + 1);
              } else {
                this.handlePageChange(1);
              }
            }}
            sortModel={
              params?.sortBy
                ? [
                    {
                      field: params.sortBy,
                      sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                    },
                  ]
                : []
            }
            onSortModelChange={(model) => {
              if (model.length > 0) {
                this.fetchBillPaymentsHeader(undefined, model[0].field);
              } else {
                this.removeSortParams();
              }
            }}
          />
        </Page.Content>
      </Page.Container>
    );
  }
}

BillPayments.propTypes = {
  location: locationProps.isRequired,
  match: matchProps.isRequired,
  history: historyProps.isRequired,
  loading: PropTypes.bool.isRequired,
  billPayments: PropTypes.arrayOf(
    PropTypes.shape({
      payment_date: PropTypes.string,
      total: PropTypes.number,
      invoice_internal_id: PropTypes.number,
      document_number: PropTypes.string,
    }),
  ),
  count: PropTypes.number,
  getBillPayments: PropTypes.func.isRequired,
};

BillPayments.defaultProps = {
  billPayments: [],
  count: 0,
};

export default BillPayments;
